import { createReducer, on } from '@ngrx/store';
import { DateTime } from 'luxon';

import { AppConstants } from '@apps/app.constants';

import * as fromAuthProfileStoreActions from './profile.actions';
import * as fromAuthLoginActions from '../login/login.actions';
import { Profile, ApplicationRole } from '../../models';

export const AuthProfileFeatureKey = 'profile';

export interface IAuthProfileState {
    profile: Profile;
    appRoles: ApplicationRole[];
    expirationDate: DateTime;
}

const initialState: IAuthProfileState = {
    profile: null,
    appRoles: null,
    expirationDate: null
};

export const AuthProfileReducer = createReducer(
    initialState,
    on(
        fromAuthLoginActions.AuthLogout,
        () => (initialState)
    ),
    on(
        fromAuthProfileStoreActions.AuthLoginProfileLoaded,
        fromAuthProfileStoreActions.AuthProfileLoaded,
        (state, { profile }) =>
        ({
            ...state,
            profile,
            expirationDate: DateTime.utc().plus({ minutes: AppConstants.DataLifeTime })
        })
    ),
    on(
        fromAuthProfileStoreActions.AuthProfileApplicationRolesSet,
        (state, { appRoles }) =>
        ({
            ...state,
            appRoles: [...appRoles]
        })
    ),
    on(
        fromAuthProfileStoreActions.AuthProfileApplicationRolesReset,
        (state) =>
        ({
            ...state,
            appRoles: null
        })
    ),
    on(
        fromAuthProfileStoreActions.AuthProfileStep1Changed,
        fromAuthProfileStoreActions.AuthProfileStep2Changed,
        fromAuthProfileStoreActions.AuthProfileStep4Changed,
        (state, { profile }) => ({
            ...state,
            profile: {
                ...state.profile,
                ...profile
            }
        })
    ),
    on(
        fromAuthProfileStoreActions.AuthProfileLanguageChanged,
        (state, { profile }) => ({
            ...state,
            profile: {
                ...state.profile,
                language: profile.language
            }
        })
    ),
    on(
        fromAuthProfileStoreActions.AuthProfileSkinChanged,
        (state, { profile }) => ({
            ...state,
            profile: {
                ...state.profile,
                skin: profile.skin
            }
        })
    ),
    on(
        fromAuthProfileStoreActions.AuthProfileInformationsChanged,
        (state, { profile }) => {
            const prev = state.profile ?? ({} as any);

            // Merge "safe": si le payload ne porte pas la donnée, on conserve l'existante
            const firstName = profile.firstName ?? prev.firstName;
            const lastName = profile.lastName ?? prev.lastName;
            const title = profile.title ?? prev.title;
            const phoneNumber = profile.phoneNumber ?? prev.phoneNumber;
            const mobile = profile.mobile ?? prev.mobile;

            return {
                ...state,
                profile: {
                    ...prev,
                    // champs "infos" classiques
                    firstName,
                    lastName,
                    title,
                    phoneNumber,
                    mobile,

                    // champs MFA (payload souvent partiel depuis l'écran Security)
                    twoFactorEnabled: (profile as any).twoFactorEnabled ?? (prev as any).twoFactorEnabled,
                    twoFactorMailEnabled: (profile as any).twoFactorMailEnabled ?? (prev as any).twoFactorMailEnabled,

                    // dérivés recalculés à partir des valeurs finales (pas à partir du payload brut)
                    firstLastName: `${firstName ?? ''} ${lastName ?? ''}`.trim(),
                    lastFirstName: `${lastName ?? ''} ${firstName ?? ''}`.trim()
                }
            };
        }
    ),
    on(
        fromAuthProfileStoreActions.AuthProfileNewsletterMarketingChanged,
        (state, { profile }) => ({
            ...state,
            profile: {
                ...state.profile,
                marketingNewsletter: profile.marketingNewsletter
            }
        })
    ),
    on(
        fromAuthProfileStoreActions.AuthProfileNewsletterNotificationChanged,
        (state, { profile }) => ({
            ...state,
            profile: {
                ...state.profile,
                maintenanceNotification: profile.maintenanceNotification
            }
        })
    ),
    on(
        fromAuthProfileStoreActions.AuthProfileSocialNetworksCookiesChanged,
        (state, { profile }) => ({
            ...state,
            profile: {
                ...state.profile,
                socialNetworksCookies: profile.socialNetworksCookies
            }
        })
    ),
    on(
        fromAuthProfileStoreActions.AuthProfileStatisticsCookiesChanged,
        (state, { profile }) => ({
            ...state,
            profile: {
                ...state.profile,
                statisticsCookies: profile.statisticsCookies
            }
        })
    ),
    on(
        fromAuthProfileStoreActions.AuthProfileWorkingCookiesChanged,
        (state, { profile }) => ({
            ...state,
            profile: {
                ...state.profile,
                workingCookies: profile.workingCookies
            }
        })
    ),
    on(
        fromAuthProfileStoreActions.AuthProfileIsCatalogSecuAuthorizedSucceeded,
        (state, { isCatalogSecuAuthorized }: { isCatalogSecuAuthorized: boolean }) => ({
            ...state,
            profile: {
                ...state.profile,
                isCatalogSecuAuthorized
            }
        })
    )
);
